import type { StepStatus } from "../../api/types";

// Step shape consumed by this component. Both the legacy SSE path and the
// new polling path produce equivalent objects — both feed into here.
export interface Step {
  name: string;
  status: StepStatus;
  detail?: string;
  started_at: number;      // ms (epoch or since job start — display-only)
  finished_at?: number;
  score?: number;
}

interface Props {
  steps: Step[];
  error: string | null;
  completed: boolean;
}

const STEP_LABELS: Record<string, string> = {
  start: "Start",
  research: "Research",
  trending: "Trending scout",
  content: "Content generation",
  guardian: "Brand Guardian review",
  tts: "TTS narration",
  whisper: "Caption alignment",
  broll: "B-roll generation",
  // `render` is generic — "Remotion render" was wrong for HTML posts.
  // Callers can pass a more specific label via the `detail` field.
  render: "Render",
};

export function ProgressTimeline({ steps, error, completed }: Props) {
  if (steps.length === 0 && !error) {
    return (
      <div className="rounded-xl border-2 border-dashed border-brand-text-dark/15 bg-white/40 p-6 text-sm text-brand-text-dark/60">
        Warte auf das erste Event vom Server…
      </div>
    );
  }

  return (
    <ol className="space-y-2">
      {steps.map((step) => (
        <StepRow key={step.name} step={step} />
      ))}
      {error ? (
        <li className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-900 ring-1 ring-red-200">
          <strong className="font-bold">Fehler:</strong> {error}
        </li>
      ) : null}
      {completed ? (
        <li className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-900 ring-1 ring-emerald-200">
          <strong className="font-bold">Fertig.</strong>{" "}
          Klick auf <em>Open post</em> in der Job-Karte rechts.
        </li>
      ) : null}
    </ol>
  );
}

function StepRow({ step }: { step: Step }) {
  const elapsed =
    step.finished_at != null
      ? Math.round(step.finished_at - step.started_at)
      : null;

  const color =
    step.status === "done"
      ? "bg-emerald-500"
      : step.status === "error"
        ? "bg-red-500"
        : step.status === "skipped"
          ? "bg-gray-400"
          : "bg-brand-blue animate-pulse";

  return (
    <li className="flex items-center gap-4 rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-black/5">
      <div className={`h-3 w-3 flex-shrink-0 rounded-full ${color}`} />
      <div className="flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-bold text-brand-text-dark">
            {STEP_LABELS[step.name] ?? step.name}
          </span>
          {step.detail ? (
            <span className="text-xs text-brand-text-dark/60">
              {step.detail}
            </span>
          ) : null}
          {step.score != null ? (
            <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800">
              score {step.score}
            </span>
          ) : null}
        </div>
      </div>
      <div className="text-xs text-brand-text-dark/50 tabular-nums">
        {elapsed != null ? `${(elapsed / 1000).toFixed(1)}s` : "…"}
      </div>
    </li>
  );
}
